/**
 * Who "we" are: the operating company's identity, in one place.
 *
 * The same code builds for Asian Trails (production) and for a demo
 * company. Every value comes from a `VITE_*` build arg (inlined by Vite at
 * build time, passed through `web/Dockerfile` ARG/ENV) and DEFAULTS to the
 * Asian Trails literal the app printed before this file existed, so a
 * build with no args renders exactly what it always did. An empty or
 * whitespace value means "use the default".
 *
 * Settings that live in the database (`company_name`, `logo_url`, the
 * hotline settings…) still win wherever the UI reads them; COMPANY is only
 * their fallback or placeholder there.
 *
 * At runtime the tenant's own profile (Admin ▸ Company, stored in
 * `hub_settings.company_profile`) is merged over the build args before the
 * app paints (`shared/companyBoot.ts`, called by `main.tsx`), so COMPANY is
 * a live object: database, then build arg, then the Asian Trails default.
 * One build can therefore serve many companies: nothing may copy a COMPANY
 * value at import time (`__tests__/companyReads.fence.test.ts` fails on one).
 *
 * The knob table (shell var / .NET key / Vite var) is the demo contract;
 * the backend reads the same profile as `Company:*`.
 */

import { useSyncExternalStore } from 'react';

export interface CompanyProfile {
  /** Short brand name: "Asian Trails". */
  readonly name: string;
  /** "Asian Trails Malaysia & Singapore". */
  readonly fullName: string;
  readonly nameMy: string;
  readonly nameSg: string;
  /** 24/7 guest hotline (phone + WhatsApp), as printed. */
  readonly hotlineMy: string;
  readonly hotlineSg: string;
  /** The company's own email domains, first = the main one. */
  readonly emailDomains: readonly string[];
  readonly website: string;
  /** Brand accent (#RRGGBB) for guest pages and exported documents. */
  readonly accent: string;
  /** Company logo override; '' = today's behaviour (server / DB logo). */
  readonly logoUrl: string;
  /**
   * Base of the guest-facing public pages (the 7-digit transfer page) AND
   * of the legacy PHP host that still serves /feedback and the old
   * `/hub/coportal/…` uploads. No trailing slash. Not saved in Admin ▸
   * Company: the build names it, else the default, else (a stack serving
   * many companies) the page's own origin (`applyCompanyHosts`).
   */
  readonly guestBaseUrl: string;
  /** Where the app itself lives. No trailing slash. Resolved like `guestBaseUrl`. */
  readonly appUrl: string;
  /** Customer-service desk addresses printed for guests. */
  readonly csEmailMy: string;
  readonly csEmailSg: string;
  /** CC on a hotel check-in email, per country. */
  readonly ccEmailMy: string;
  readonly ccEmailSg: string;
  /** Registered legal entities (PDPA statement footer). */
  readonly legalNameMy: string;
  readonly legalNameSg: string;
  /** The address Freya's reports are sent from. */
  readonly agentEmail: string;
  /** Booking reference labels per country ("Ref ATMA", "Ref ATS"). */
  readonly refLabelMy: string;
  readonly refLabelSg: string;
  /** The guest page's sign-off line. */
  readonly signOff: string;
  /**
   * The key partner agency, by its name as it appears in the agent column
   * ("Kuoni"): its contacts book, refs, courtesy calls and monthly report.
   * Labels and client-side agent filters read it; API routes, query keys
   * and field names (`kuoni_ref`, `kuoni_contacts`) do not change.
   */
  readonly keyAgent: string;
}

/** The Asian Trails values: the production build, pinned by tests. */
export const COMPANY_DEFAULTS: CompanyProfile = Object.freeze({
  name: 'Asian Trails',
  fullName: 'Asian Trails Malaysia & Singapore',
  nameMy: 'Asian Trails Malaysia',
  nameSg: 'Asian Trails Singapore',
  hotlineMy: '+60 19 354 0590',
  hotlineSg: '+65 8118 5883',
  emailDomains: Object.freeze(['asiantrails.com.my', 'asiantrails.sg', 'at-mysg.com', 'asiantrails.com']),
  website: 'https://at-mysg.com',
  accent: '#FF6600',
  logoUrl: '',
  guestBaseUrl: 'https://at-mysg.com',
  appUrl: 'https://at-mysg.amassa.ai',
  csEmailMy: 'customerservice@asiantrails.com.my',
  csEmailSg: 'customerservice@asiantrails.sg',
  ccEmailMy: 'cs@asiantrails.com.my',
  ccEmailSg: 'cs@asiantrails.sg',
  legalNameMy: 'Asian Trails (Malaysia) Sdn Bhd',
  legalNameSg: 'Asian Trails (Singapore) Pte Ltd',
  agentEmail: 'agent@at-mysg.com',
  refLabelMy: 'ATMA',
  refLabelSg: 'ATS',
  signOff: 'Happy Trails!',
  keyAgent: 'Kuoni',
});

/** The raw build args, by Vite name. */
export type CompanyEnv = Partial<Record<string, string | boolean | undefined>>;

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const pick = (v: unknown, fallback: string): string => text(v) || fallback;
const noTrailingSlash = (s: string): string => s.replace(/\/+$/, '');

/**
 * A list of email domains as written ("A.example, @b.example; c.example"):
 * lower case, no leading "@", no blanks or repeats, in the written order.
 * The API reads `Company:EmailDomains` and the saved field the same way.
 */
export function splitDomains(raw: unknown): string[] {
  const out: string[] = [];
  for (const part of text(raw).split(/[\s,;]+/)) {
    const d = part.replace(/^@+/, '').toLowerCase();
    if (d && !out.includes(d)) out.push(d);
  }
  return out;
}

/**
 * Build the profile from build args. Pure, so tests can feed it any env.
 * `VITE_TENANT_NAME` (the older knob) is honoured as an alias of
 * `VITE_COMPANY_FULL_NAME`; the new name wins when both are set.
 */
export function buildCompany(env: CompanyEnv): CompanyProfile {
  const d = COMPANY_DEFAULTS;
  const domains = splitDomains(env.VITE_COMPANY_EMAIL_DOMAINS);
  return Object.freeze({
    name: pick(env.VITE_COMPANY_NAME, d.name),
    fullName: pick(env.VITE_COMPANY_FULL_NAME, pick(env.VITE_TENANT_NAME, d.fullName)),
    nameMy: pick(env.VITE_COMPANY_NAME_MY, d.nameMy),
    nameSg: pick(env.VITE_COMPANY_NAME_SG, d.nameSg),
    hotlineMy: pick(env.VITE_COMPANY_HOTLINE_MY, d.hotlineMy),
    hotlineSg: pick(env.VITE_COMPANY_HOTLINE_SG, d.hotlineSg),
    emailDomains: domains.length > 0 ? Object.freeze(domains) : d.emailDomains,
    website: noTrailingSlash(pick(env.VITE_COMPANY_WEBSITE, d.website)),
    accent: pick(env.VITE_COMPANY_ACCENT, d.accent),
    logoUrl: pick(env.VITE_COMPANY_LOGO_URL, d.logoUrl),
    guestBaseUrl: noTrailingSlash(pick(env.VITE_GUEST_BASE_URL, d.guestBaseUrl)),
    appUrl: noTrailingSlash(pick(env.VITE_APP_URL, d.appUrl)),
    csEmailMy: pick(env.VITE_COMPANY_CS_EMAIL_MY, d.csEmailMy),
    csEmailSg: pick(env.VITE_COMPANY_CS_EMAIL_SG, d.csEmailSg),
    ccEmailMy: pick(env.VITE_COMPANY_CC_EMAIL_MY, d.ccEmailMy),
    ccEmailSg: pick(env.VITE_COMPANY_CC_EMAIL_SG, d.ccEmailSg),
    legalNameMy: pick(env.VITE_COMPANY_LEGAL_NAME_MY, d.legalNameMy),
    legalNameSg: pick(env.VITE_COMPANY_LEGAL_NAME_SG, d.legalNameSg),
    agentEmail: pick(env.VITE_COMPANY_AGENT_EMAIL, d.agentEmail),
    refLabelMy: pick(env.VITE_COMPANY_REF_LABEL_MY, d.refLabelMy),
    refLabelSg: pick(env.VITE_COMPANY_REF_LABEL_SG, d.refLabelSg),
    signOff: pick(env.VITE_COMPANY_SIGN_OFF, d.signOff),
    keyAgent: pick(env.VITE_COMPANY_KEY_AGENT, d.keyAgent),
  });
}

/**
 * Read each var by its literal name: Vite inlines `import.meta.env.VITE_X`
 * at build time, and vitest's `vi.stubEnv` reaches the same reads.
 */
function readEnv(): CompanyEnv {
  return {
    VITE_COMPANY_NAME: import.meta.env.VITE_COMPANY_NAME,
    VITE_COMPANY_FULL_NAME: import.meta.env.VITE_COMPANY_FULL_NAME,
    VITE_TENANT_NAME: import.meta.env.VITE_TENANT_NAME,
    VITE_COMPANY_NAME_MY: import.meta.env.VITE_COMPANY_NAME_MY,
    VITE_COMPANY_NAME_SG: import.meta.env.VITE_COMPANY_NAME_SG,
    VITE_COMPANY_HOTLINE_MY: import.meta.env.VITE_COMPANY_HOTLINE_MY,
    VITE_COMPANY_HOTLINE_SG: import.meta.env.VITE_COMPANY_HOTLINE_SG,
    VITE_COMPANY_EMAIL_DOMAINS: import.meta.env.VITE_COMPANY_EMAIL_DOMAINS,
    VITE_COMPANY_WEBSITE: import.meta.env.VITE_COMPANY_WEBSITE,
    VITE_COMPANY_ACCENT: import.meta.env.VITE_COMPANY_ACCENT,
    VITE_COMPANY_LOGO_URL: import.meta.env.VITE_COMPANY_LOGO_URL,
    VITE_GUEST_BASE_URL: import.meta.env.VITE_GUEST_BASE_URL,
    VITE_APP_URL: import.meta.env.VITE_APP_URL,
    VITE_COMPANY_CS_EMAIL_MY: import.meta.env.VITE_COMPANY_CS_EMAIL_MY,
    VITE_COMPANY_CS_EMAIL_SG: import.meta.env.VITE_COMPANY_CS_EMAIL_SG,
    VITE_COMPANY_CC_EMAIL_MY: import.meta.env.VITE_COMPANY_CC_EMAIL_MY,
    VITE_COMPANY_CC_EMAIL_SG: import.meta.env.VITE_COMPANY_CC_EMAIL_SG,
    VITE_COMPANY_LEGAL_NAME_MY: import.meta.env.VITE_COMPANY_LEGAL_NAME_MY,
    VITE_COMPANY_LEGAL_NAME_SG: import.meta.env.VITE_COMPANY_LEGAL_NAME_SG,
    VITE_COMPANY_AGENT_EMAIL: import.meta.env.VITE_COMPANY_AGENT_EMAIL,
    VITE_COMPANY_REF_LABEL_MY: import.meta.env.VITE_COMPANY_REF_LABEL_MY,
    VITE_COMPANY_REF_LABEL_SG: import.meta.env.VITE_COMPANY_REF_LABEL_SG,
    VITE_COMPANY_SIGN_OFF: import.meta.env.VITE_COMPANY_SIGN_OFF,
    VITE_COMPANY_KEY_AGENT: import.meta.env.VITE_COMPANY_KEY_AGENT,
  };
}

const BUILD_ENV = readEnv();

/** The profile this bundle was built for (its build args over the defaults). */
export const COMPANY_BUILD: CompanyProfile = buildCompany(BUILD_ENV);

/**
 * Which of the two addresses the build names itself (`VITE_GUEST_BASE_URL`,
 * `VITE_APP_URL`). A named one is never replaced at runtime; an unnamed one
 * may follow the page's origin (see `applyCompanyHosts`).
 */
export const COMPANY_BUILD_HOSTS: Readonly<{ guest: boolean; app: boolean }> = Object.freeze({
  guest: text(BUILD_ENV.VITE_GUEST_BASE_URL) !== '',
  app: text(BUILD_ENV.VITE_APP_URL) !== '',
});

// ─── The runtime profile (Admin ▸ Company, brief §8.0) ─────────────────
//
// A tenant saves its own profile in `hub_settings.company_profile`. The API
// serves the SAVED values only (`GET /api/public/company` before the app
// paints, `GET /api/company` once signed in, next to the module tree), and
// they are merged here over the build profile: database, then build arg, then
// Asian Trails. With no row nothing is merged, and COMPANY is COMPANY_BUILD
// value for value (Asian Trails unchanged).

/** The fields a tenant can save in Admin ▸ Company (the API's CompanyProfileOverrides.Fields). */
export const COMPANY_EDITABLE = [
  'name',
  'fullName',
  'nameMy',
  'nameSg',
  'legalNameMy',
  'legalNameSg',
  'hotlineMy',
  'hotlineSg',
  'csEmailMy',
  'csEmailSg',
  'ccEmailMy',
  'ccEmailSg',
  'website',
  'logoUrl',
  'accent',
  'signOff',
  'refLabelMy',
  'refLabelSg',
  'keyAgent',
  'emailDomains',
  'agentEmail',
] as const satisfies readonly (keyof CompanyProfile)[];

export type CompanyEditableKey = (typeof COMPANY_EDITABLE)[number];

/**
 * What the API sends: saved values by field, every one a string (the
 * API-only `legalName` is ignored here). `emailDomains` travels as the list
 * written out ("a.example, b.example") and becomes the profile's array on merge.
 */
export type CompanyOverrides = Partial<Record<CompanyEditableKey, string>>;

/** Served without sign-in (the API's `Public` flag): what the login and guest pages print. */
export const COMPANY_PUBLIC: readonly CompanyEditableKey[] = [
  'name',
  'fullName',
  'nameMy',
  'nameSg',
  'legalNameMy',
  'legalNameSg',
  'hotlineMy',
  'hotlineSg',
  'csEmailMy',
  'csEmailSg',
  'website',
  'logoUrl',
  'accent',
  'signOff',
  // The sign-in page suggests an address on the main domain.
  'emailDomains',
];

const HEX_RE = /^#[0-9a-f]{6}$/i;

/**
 * The saved values, read leniently: known keys only, strings only, trimmed,
 * blanks dropped, an accent that is not #RRGGBB dropped (it is painted into
 * guest pages) and the email domains written out as one tidy list. Anything
 * else (null, an array, a number) is `{}`.
 */
export function normalizeCompanyOverrides(raw: unknown): CompanyOverrides {
  const out: CompanyOverrides = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  const src = raw as Record<string, unknown>;
  for (const k of COMPANY_EDITABLE) {
    const v = text(src[k]);
    if (!v) continue;
    if (k === 'accent' && !HEX_RE.test(v)) continue;
    if (k === 'emailDomains') {
      const list = splitDomains(v);
      if (list.length > 0) out[k] = list.join(', ');
      continue;
    }
    out[k] = k === 'website' ? noTrailingSlash(v) : v;
  }
  return out;
}

/** `base` with every saved value written over it. Pure. */
export function mergeCompany(base: CompanyProfile, overrides: CompanyOverrides): CompanyProfile {
  const o = normalizeCompanyOverrides(overrides);
  if (Object.keys(o).length === 0) return base;
  const { emailDomains, ...rest } = o;
  return Object.freeze({
    ...base,
    ...rest,
    ...(emailDomains ? { emailDomains: Object.freeze(splitDomains(emailDomains)) } : {}),
  });
}

// ─── Where the app and its guest pages live ────────────────────────────
//
// `guestBaseUrl` and `appUrl` are not saved in Admin ▸ Company: they are
// where this copy of the app is served. A build names them (the demo's
// VITE_GUEST_BASE_URL / VITE_APP_URL), or they are the code defaults, which
// are Asian Trails' hosts (its legacy PHP guest host stays at-mysg.com while
// the app moves). A stack that serves many companies, each on its own host,
// says so in its company answer (`urls: "origin"`, the API's Tenancy:Mode
// multi): every address the build does not name then follows the page's own
// origin. A one-company stack never sends it, so Asian Trails is unchanged.

let followOrigin = false;

function pageOrigin(): string {
  try {
    return typeof window !== 'undefined' && window.location?.origin && window.location.origin !== 'null'
      ? noTrailingSlash(window.location.origin)
      : '';
  } catch {
    return '';
  }
}

/**
 * `profile` with the addresses its build does not name moved to `origin`.
 * Pure. The same object when there is nothing to move.
 */
export function withOriginHosts(
  profile: CompanyProfile,
  origin: string,
  named: { guest: boolean; app: boolean } = COMPANY_BUILD_HOSTS,
): CompanyProfile {
  const o = noTrailingSlash(text(origin));
  if (!o) return profile;
  const guestBaseUrl = named.guest ? profile.guestBaseUrl : o;
  const appUrl = named.app ? profile.appUrl : o;
  if (guestBaseUrl === profile.guestBaseUrl && appUrl === profile.appUrl) return profile;
  return Object.freeze({ ...profile, guestBaseUrl, appUrl });
}

let saved: CompanyOverrides = {};
let current: CompanyProfile = COMPANY_BUILD;
const listeners = new Set<() => void>();

/** The build profile, the saved values over it, then the hosts. */
function resolve(): CompanyProfile {
  const merged = mergeCompany(COMPANY_BUILD, saved);
  return followOrigin ? withOriginHosts(merged, pageOrigin()) : merged;
}

function publish(): CompanyProfile {
  const profile = resolve();
  if (!sameProfile(profile, current)) {
    current = profile;
    for (const fn of listeners) fn();
  }
  return current;
}

/**
 * Apply what the API sent. `scope: 'public'` (the anonymous bootstrap)
 * replaces the public fields only and keeps any staff field already known;
 * `'all'` (signed in, or Admin ▸ Company after a save) replaces every field.
 */
export function applyCompanyOverrides(raw: unknown, scope: 'public' | 'all' = 'all'): CompanyProfile {
  const incoming = normalizeCompanyOverrides(raw);
  let next: CompanyOverrides;
  if (scope === 'all') {
    next = incoming;
  } else {
    next = { ...saved };
    for (const k of COMPANY_PUBLIC) delete next[k];
    for (const k of COMPANY_PUBLIC) if (incoming[k]) next[k] = incoming[k];
  }
  saved = next;
  return publish();
}

/**
 * Apply the `urls` of the company answer: `"origin"` (a stack that serves
 * many companies) moves the addresses the build does not name to the page's
 * origin; anything else keeps the build's. The answer of a one-company stack
 * has no `urls`, and the caller then does not call this.
 */
export function applyCompanyHosts(raw: unknown): CompanyProfile {
  followOrigin = raw === 'origin';
  return publish();
}

/** Back to the build profile and the build's addresses (tests). */
export function resetCompany(): void {
  followOrigin = false;
  applyCompanyOverrides({}, 'all');
}

/** The saved values currently applied. */
export function getCompanyOverrides(): CompanyOverrides {
  return { ...saved };
}

/** Where a field's value comes from: saved in Admin ▸ Company, a build arg, or the code default. */
export function companySource(key: CompanyEditableKey): 'saved' | 'build' | 'default' {
  if (saved[key]) return 'saved';
  return COMPANY_BUILD[key] !== COMPANY_DEFAULTS[key] ? 'build' : 'default';
}

/** The running profile (an immutable snapshot; a new object after every change). */
export function getCompany(): CompanyProfile {
  return current;
}

export function subscribeCompany(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function sameProfile(a: CompanyProfile, b: CompanyProfile): boolean {
  if (a === b) return true;
  return (Object.keys(COMPANY_DEFAULTS) as (keyof CompanyProfile)[]).every((k) => {
    const x = a[k];
    const y = b[k];
    return Array.isArray(x) && Array.isArray(y) ? x.join(',') === y.join(',') : x === y;
  });
}

const KEYS = Object.keys(COMPANY_DEFAULTS) as (keyof CompanyProfile)[];

/**
 * The running profile, as one object every call site reads (`COMPANY.name`).
 * Its fields are getters over the current snapshot, so a read always sees
 * the hydrated profile, and the object itself is frozen (nothing can write
 * it). React components that must re-render on a change read `useCompany()`
 * (below); everything else reads COMPANY at call time.
 *
 * A value copied at import time (`const X = COMPANY.name` at module scope)
 * keeps the build value: keep reads inside functions.
 */
export const COMPANY: CompanyProfile = Object.freeze(
  Object.defineProperties(
    {},
    Object.fromEntries(KEYS.map((k) => [k, { get: () => current[k], enumerable: true }])),
  ) as CompanyProfile,
);

/** The running profile, re-rendering the component when it changes (a save in Admin ▸ Company). */
export function useCompany(): CompanyProfile {
  return useSyncExternalStore(subscribeCompany, getCompany, getCompany);
}

/**
 * The workspace's name as the sidebar prints it under the mark: the short
 * name once the company is named (saved in Admin ▸ Company, or a build arg),
 * null while it is the code default (Asian Trails' install, unchanged).
 */
export function workspaceName(profile: CompanyProfile = current, overrides: CompanyOverrides = saved): string | null {
  if (overrides.name) return profile.name;
  return COMPANY_BUILD.name !== COMPANY_DEFAULTS.name ? profile.name : null;
}

export function useWorkspaceName(): string | null {
  const profile = useCompany();
  return workspaceName(profile);
}

/**
 * Who answers on Telegram, by name, in copy. Freya is Asian Trails' bot, so
 * she is named only while the company is Asian Trails (the code default);
 * any other company is on the shared Amassa bot (brief §9.2), and the copy
 * says "Amassa", as Preferences ▸ Connect Telegram already does in
 * simulation. The API's bot rights follow the same rule
 * (CompanyProfile.RebrandBot).
 */
export function botName(profile: CompanyProfile = COMPANY): string {
  return profile.name === COMPANY_DEFAULTS.name ? 'Freya' : 'Amassa';
}

/** "+60 19 354 0590" → "60193540590", for a `https://wa.me/<digits>` link. */
export function waDigits(phone: string): string {
  return phone.replace(/\D+/g, '');
}

/**
 * A sample address on the company's domain, for placeholders. `atDomain` is
 * the domain the Asian Trails build printed; it is kept when the profile
 * lists it (so production reads exactly as before), else the profile's main
 * domain stands in.
 */
export function companyEmail(local: string, atDomain: string, profile: CompanyProfile = COMPANY): string {
  const domain = profile.emailDomains.includes(atDomain.toLowerCase())
    ? atDomain
    : (profile.emailDomains[0] ?? atDomain);
  return `${local}@${domain}`;
}

/** Every word-bounded mention of the key agent, any case, for client filters. */
export function keyAgentRe(flags = 'i', profile: CompanyProfile = COMPANY): RegExp {
  const esc = profile.keyAgent.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${esc}\\b`, flags);
}
